// src/pages/SubscriberAddBeneficiaryPage.jsx
// Route: /subscriber/add-beneficiary
//
// No Figma frame exists for this screen. Restyled onto the Navigator
// portal's shared .page-header/.back-link/.form-section/.form-grid/
// .form-input/.btn-accent classes (see PatientFormPage.js) instead of the
// bespoke inputStyle/labelStyle objects this screen used before.
//
// Beneficiaries are always Kerala parents (per Guardian_Dubai_Operating_
// Manual_v1.0) — phone is always an Indian number here regardless of the
// subscriber's own country, matching SubscriberBeneficiaryService's backend
// validation.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubscriberAppShell } from '../components/SubscriberLayout';
import { useSubscriber } from '../context/SubscriberContext';
import { subscriberAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { displayPatientCode } from '../utils/patientCode';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function SubscriberAddBeneficiaryPage() {
  const { subscriber } = useSubscriber();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');
  const [relationship, setRelationship] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!consentGiven) {
      setError('Please confirm you have your family member’s consent to enroll them.');
      return;
    }
    if (dateOfBirth && dateOfBirth > todayISO()) {
      setError('Date of birth cannot be in the future.');
      return;
    }
    setError(''); setSubmitting(true);
    try {
      const res = await subscriberAPI.addBeneficiary(subscriber.subscriberId, {
        firstName, lastName, phone, dateOfBirth, gender, relationship, consentGiven,
      });
      // The new (or matched) beneficiary's code, so the subscriber has it to
      // quote — dropped when the server sends none.
      const code = displayPatientCode(res.data.patientCode);
      toast.success(
        (res.data.wasExistingPatient
          ? 'Linked to your existing family record.'
          : 'Family member added!')
        + (code ? ` Patient ID: ${code}` : '')
      );
      navigate('/subscriber/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Could not add this family member.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SubscriberAppShell breadcrumbs={[
      { label: 'My Family', to: '/subscriber/dashboard' },
      { label: 'Add family member' },
    ]}>
      <span className="back-link fade-in-up" onClick={() => navigate('/subscriber/dashboard')}>
        ← Back to My Family
      </span>

      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">Add a family member</div>
          <div className="page-subtitle">
            Your plan covers up to {subscriber?.maxBeneficiaries || 1} family member{subscriber?.maxBeneficiaries === 2 ? 's' : ''}.
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error fade-in-up">⚠️ {error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section fade-in-up delay-1" style={{ maxWidth: 480 }}>
          <div className="form-section-title">👤 Family Member Details</div>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">First name <span className="required">*</span></label>
              <input className="form-input" value={firstName}
                onChange={e => setFirstName(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Last name <span className="required">*</span></label>
              <input className="form-input" value={lastName}
                onChange={e => setLastName(e.target.value)} required />
            </div>

            <div className="form-group form-full">
              <label className="form-label">Phone (India) <span className="required">*</span></label>
              <input className="form-input" value={phone} type="tel"
                placeholder="9876543210" onChange={e => setPhone(e.target.value)} required />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                10-digit Indian mobile number. No need to add +91.
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">Date of birth <span className="required">*</span></label>
              <input className="form-input" type="date" max={todayISO()}
                value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} required />
            </div>
            <div className="form-group">
              <label className="form-label">Gender <span className="required">*</span></label>
              <select className="form-select" value={gender}
                onChange={e => setGender(e.target.value)} required>
                <option value="">Select</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div className="form-group form-full">
              <label className="form-label">Relationship (optional)</label>
              <input className="form-input" value={relationship}
                placeholder="Mother, Father…" onChange={e => setRelationship(e.target.value)} />
            </div>
          </div>

          <label style={{
            display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13,
            color: 'var(--text-secondary)', marginTop: 20,
          }}>
            <input type="checkbox" checked={consentGiven}
              onChange={e => setConsentGiven(e.target.checked)}
              style={{ marginTop: 3 }} />
            <span>
              I confirm I have my family member's consent to enroll them with Previu and share their health
              information with their Navigator and care team.
            </span>
          </label>

          <div className="form-actions">
            <button type="submit" className="btn btn-accent" disabled={submitting}>
              {submitting ? 'Adding…' : 'Add family member'}
            </button>
          </div>
        </div>
      </form>
    </SubscriberAppShell>
  );
}
